import type { ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useSession } from './auth/SessionProvider'
import { Button } from './ui'
import { ROLE_LABELS, REPORT_VIEWER_ROLES } from '@shared/schema/api'
import type { Role } from '@shared/schema/api'

/** เมนูของแต่ละบทบาท — ผู้ดูรายงานเห็นแค่หน้าผลตอบกลับหน้าเดียว */
type NavItem = { to: string; label: string; roles: readonly Role[] }

const NAV_ITEMS: NavItem[] = [
  { to: '/workflows', label: 'แบบสอบถาม', roles: ['admin'] },
  { to: '/run', label: 'ทำแบบสอบถาม', roles: ['admin', 'field'] },
  { to: '/dashboard', label: 'ภาพรวม', roles: ['admin', 'report'] },
  { to: '/submissions', label: 'ผลตอบกลับ', roles: ['admin', 'report'] },
  { to: '/users', label: 'ผู้ใช้งาน', roles: ['admin'] },
]

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useSession()
  const navigate = useNavigate()

  const items = user ? NAV_ITEMS.filter((item) => item.roles.includes(user.role)) : []
  const canSeeReports = user ? REPORT_VIEWER_ROLES.includes(user.role) : false

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-4 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--fs-primary)] text-xs font-bold text-white">
              MT
            </span>
            <span className="hidden text-sm font-semibold text-slate-800 sm:inline">
              Flow Studio
            </span>
          </div>

          <nav className="flex flex-1 gap-1 overflow-x-auto">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? 'bg-[var(--fs-primary)] text-[var(--fs-on-primary)]'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {/* แดชบอร์ดหน้าตาเดิมของ Phase 1 — เป็นหน้า static คนละก้อนกับแอปนี้ จึงใช้ <a> ไม่ใช่ NavLink */}
            {canSeeReports && (
              <a
                href="/legacy-dashboard/"
                className="whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
              >
                แดชบอร์ด (แบบเดิม)
              </a>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden text-end sm:block">
                <p className="text-sm font-medium text-slate-800">{user.name}</p>
                <p className="text-xs text-slate-500">{ROLE_LABELS[user.role]}</p>
              </div>
            )}
            <Button
              variant="ghost"
              onClick={async () => {
                await logout()
                navigate('/login', { replace: true })
              }}
            >
              ออกจากระบบ
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>
    </div>
  )
}
